<template>
  <g>
    <!-- outer decorative rectangles -->
    <rect v-for="rect in rects" :key="rect.key + '-outer'"
      :x="rect.outerX" :y="rect.outerY"
      :width="rect.outerWidth" :height="rect.outerHeight"
      :stroke="colorHex" stroke-width="0.01" fill="none"
    />

    <!-- Border lines -->
    <line v-for="line in lines" :key="line.key"
      :x1="line.x1" :y1="line.y1"
      :x2="line.x2" :y2="line.y2"
      :stroke="colorHex" stroke-width="0.01"
    />

    <!-- Corner stars -->
    <svg v-for="star in stars" :key="star.key"
      :x="star.x" :y="star.y"
      :width="starSize" :height="starSize" viewBox="0 0 500 500"
      xmlns="http://www.w3.org/2000/svg">
      <path d="M250 15
               C265 150 350 235 485 250
               C350 265 265 350 250 485
               C235 350 150 265 15 250
               C150 235 235 150 250 15 Z"
            :fill="colorHex"
            :stroke="colorHex"
            stroke-width="20"
            stroke-linejoin="round"/>
    </svg>

    <!-- Inner rects -->
    <rect v-for="rect in rects" :key="rect.key + '-inner'"
      :x="rect.innerX" :y="rect.innerY"
      :width="rect.innerWidth" :height="rect.innerHeight"
      :stroke="colorHex" stroke-width="0.01" fill="none"
    />

    <!-- Corner flourishes -->
    <svg v-for="flourish in flourishes" :key="flourish.key"
      :x="flourish.x" :y="flourish.y"
      :width="flourishSize" :height="flourishSize" viewBox="0 0 250 250"
      xmlns="http://www.w3.org/2000/svg">
      <g :transform="flourish.transform">
        <path d="M250 0
                 C235 150 150 235 0 250
                 L250 250 Z"
              :fill="colorHex"/>
      </g>
    </svg>
  </g>
</template>

<script>
// Distance from the artwork opening edge to the inner decorative rect (in inches)
const INNER_OFFSET = 0.25;
// Width and height of each corner flourish (in inches)
const FLOURISH_SIZE = 0.1875;
// Width and height of each corner star diamond (in inches)
const STAR_SIZE = 0.1875;
// Gap between the artwork opening edge and the center of a star (in inches)
const STAR_OFFSET = 0.3125;
// Distance from the artwork opening edge to the border line (in inches) — aligned with star center
const LINE_OFFSET = STAR_OFFSET + (STAR_SIZE / 2);
// Distance between the center of the star and the end of the border line (in inches)
const LINE_GAP = 0.3125;
// Distance from the artwork opening edge to the outer decorative rectangle (in inches)
const OUTER_OFFSET = LINE_OFFSET + (STAR_SIZE / 2) + 0.0625;

export default {
  props: {
    artworkOpenings: {
      type: Array,
      required: true
    },
    decorationColor: {
      type: String,
      default: "black"
    },
  },
  computed: {
    colorHex() {
      return this.decorationColor === 'white' ? '#ffffff' : '#050708';
    },
    starSize() {
      return STAR_SIZE;
    },
    flourishSize() {
      return FLOURISH_SIZE;
    },
    stars() {
      return this.artworkOpenings.flatMap((opening) => [
        { key: `${opening.key}-star-tl`, x: opening.x - STAR_OFFSET - STAR_SIZE, y: opening.y - STAR_OFFSET - STAR_SIZE },
        { key: `${opening.key}-star-tr`, x: opening.x + opening.width + STAR_OFFSET, y: opening.y - STAR_OFFSET - STAR_SIZE },
        { key: `${opening.key}-star-br`, x: opening.x + opening.width + STAR_OFFSET, y: opening.y + opening.height + STAR_OFFSET },
        { key: `${opening.key}-star-bl`, x: opening.x - STAR_OFFSET - STAR_SIZE, y: opening.y + opening.height + STAR_OFFSET },
      ]);
    },
    flourishes() {
      return this.artworkOpenings.flatMap((opening) => {
        const left = opening.x;
        const top = opening.y;
        const right = opening.x + opening.width;
        const bottom = opening.y + opening.height;

        return [
          { key: `${opening.key}-fl-tl`, x: left - INNER_OFFSET, y: top - INNER_OFFSET, transform: 'rotate(180, 125, 125)' },
          { key: `${opening.key}-fl-tr`, x: right + INNER_OFFSET - FLOURISH_SIZE, y: top - INNER_OFFSET, transform: 'rotate(270, 125, 125)' },
          { key: `${opening.key}-fl-br`, x: right + INNER_OFFSET - FLOURISH_SIZE, y: bottom + INNER_OFFSET - FLOURISH_SIZE, transform: 'rotate(0, 125, 125)' },
          { key: `${opening.key}-fl-bl`, x: left - INNER_OFFSET, y: bottom + INNER_OFFSET - FLOURISH_SIZE, transform: 'rotate(90, 125, 125)' },
        ];
      });
    },
    lines() {
      return this.artworkOpenings.flatMap((opening) => {
        const left = opening.x;
        const top = opening.y;
        const right = opening.x + opening.width;
        const bottom = opening.y + opening.height;
        // Distance from opening edge to line endpoint — star center + gap
        const lineEnd = STAR_OFFSET + (STAR_SIZE / 2) - LINE_GAP;

        return [
          { key: `${opening.key}-line-top`, x1: left - lineEnd, y1: top - LINE_OFFSET, x2: right + lineEnd, y2: top - LINE_OFFSET },
          { key: `${opening.key}-line-right`, x1: right + LINE_OFFSET, y1: top - lineEnd, x2: right + LINE_OFFSET, y2: bottom + lineEnd },
          { key: `${opening.key}-line-bottom`, x1: left - lineEnd, y1: bottom + LINE_OFFSET, x2: right + lineEnd, y2: bottom + LINE_OFFSET },
          { key: `${opening.key}-line-left`, x1: left - LINE_OFFSET, y1: top - lineEnd, x2: left - LINE_OFFSET, y2: bottom + lineEnd },
        ];
      });
    },
    rects() {
      return this.artworkOpenings.map((opening) => ({
        key: opening.key,
        outerX: opening.x - OUTER_OFFSET,
        outerY: opening.y - OUTER_OFFSET,
        outerWidth: opening.width + (OUTER_OFFSET * 2),
        outerHeight: opening.height + (OUTER_OFFSET * 2),
        innerX: opening.x - INNER_OFFSET,
        innerY: opening.y - INNER_OFFSET,
        innerWidth: opening.width + (INNER_OFFSET * 2),
        innerHeight: opening.height + (INNER_OFFSET * 2),
      }));
    }
  }
};
</script>
